import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import JSZip from 'jszip';
import { FiUpload, FiX, FiChevronLeft, FiChevronRight, FiDownload, FiTrash2 } from 'react-icons/fi';
import DeleteConfirmationModal from './DeleteConfirmationModal.jsx';

const API_BASE = process.env.REACT_APP_API_URL || '';

const MEDIA_TYPE_TO_FOLDER = {
    wechatQR: 'w',
    standardPhotos: 'sp',
    icecreamPhotos: 'icp',
    standardVideos: 'sv',
    icecreamVideos: 'icv',
};

function getMediaDisplayUrl(memberId, mediaType, fullUrl, apiResource = 'godmembers') {
    if (!memberId || !mediaType || !fullUrl) return fullUrl || '';
    const folder = MEDIA_TYPE_TO_FOLDER[mediaType] || mediaType;
    const filename = (fullUrl.split('/').pop() || '').split('?')[0];
    if (!filename) return fullUrl;
    const base = API_BASE ? API_BASE.replace(/\/$/, '') : '';
    return `${base}/api/${apiResource}/${memberId}/media/${folder}/${encodeURIComponent(filename)}`;
}

const MEDIA_SECTIONS = [
    { key: 'wechatQR', label: 'WeChat QR', accept: 'image/*', isVideo: false },
    { key: 'standardPhotos', label: 'Standard Photos', accept: 'image/*', isVideo: false },
    { key: 'icecreamPhotos', label: 'Icecream Photos', accept: 'image/*', isVideo: false },
    { key: 'standardVideos', label: 'Standard Videos', accept: 'video/*', isVideo: true },
    { key: 'icecreamVideos', label: 'Icecream Videos', accept: 'video/*', isVideo: true },
];

const THUMB_SIZE = 'w-32 h-32';

/** Title left: "Media - " then identifiers in blue (god member or non-member). */
function MediaTitleLeft({ member, apiResource = 'godmembers' }) {
    if (!member) return <span>Media</span>;
    if (apiResource === 'nonmembers') {
        const n = member.name || '';
        const w = member.webID || '';
        return (
            <>
                Media - <span className="text-blue-700 font-semibold">{n} - {w}</span>
            </>
        );
    }
    const { godID, godName, webID, oldNames } = member;
    const lastOldName = oldNames?.length ? oldNames[oldNames.length - 1] : null;
    const g = godID || '';
    const n = godName || '';
    const w = webID || '';
    const namePart = lastOldName && lastOldName !== 'N/A' ? `${lastOldName}(${n})` : n;
    return (
        <>
            Media - <span className="text-blue-700 font-semibold">{g} - {namePart} - {w}</span>
        </>
    );
}

/** Renewal date style: past = red, future = green */
function getRenewalDateStyle(renewalDate) {
    if (!renewalDate) return 'text-gray-600';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const renewal = new Date(renewalDate);
    renewal.setHours(0, 0, 0, 0);
    return renewal < today ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold';
}

function formatDate(d) {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString();
}

/**
 * View/edit media modal: 5 columns, upload same size as thumb, lightbox with keyboard prev/next across all media.
 */
const GodViewMediaModal = ({ member, onClose, onMediaUpdate, apiResource = 'godmembers' }) => {
    const memberId = member?._id;
    const [urlsByType, setUrlsByType] = useState({
        wechatQR: member?.wechatQR || [],
        standardPhotos: member?.standardPhotos || [],
        icecreamPhotos: member?.icecreamPhotos || [],
        standardVideos: member?.standardVideos || [],
        icecreamVideos: member?.icecreamVideos || [],
    });

    useEffect(() => {
        if (!member) return;
        setUrlsByType({
            wechatQR: member.wechatQR || [],
            standardPhotos: member.standardPhotos || [],
            icecreamPhotos: member.icecreamPhotos || [],
            standardVideos: member.standardVideos || [],
            icecreamVideos: member.icecreamVideos || [],
        });
    }, [member?._id, member?.wechatQR, member?.standardPhotos, member?.icecreamPhotos, member?.standardVideos, member?.icecreamVideos]);

    useEffect(() => {
        if (!memberId) return;
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' };
        axios
            .get(`${API_BASE}/api/${apiResource}/${memberId}/media`, { headers })
            .then((res) => {
                if (res.data?.success && res.data?.data) {
                    const m = res.data.data;
                    setUrlsByType({
                        wechatQR: m.wechatQR || [],
                        standardPhotos: m.standardPhotos || [],
                        icecreamPhotos: m.icecreamPhotos || [],
                        standardVideos: m.standardVideos || [],
                        icecreamVideos: m.icecreamVideos || [],
                    });
                }
            })
            .catch(() => {});
    }, [memberId, apiResource]);

    const [uploading, setUploading] = useState({});
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [downloadingKey, setDownloadingKey] = useState(null); // mediaType when downloading that section
    const [dragOverKey, setDragOverKey] = useState(null);
    const [lightboxIndex, setLightboxIndex] = useState(null); // index into allMediaItems, or null when closed
    const [deleteAllTarget, setDeleteAllTarget] = useState(null); // { key, label } | null
    const [deleteAllLoading, setDeleteAllLoading] = useState(false);
    const inputRefs = useRef({});

    // Flat list of all media items for lightbox navigation (order: wechatQR, standardPhotos, icecreamPhotos, standardVideos, icecreamVideos)
    const allMediaItems = useMemo(() => {
        if (!memberId) return [];
        const items = [];
        MEDIA_SECTIONS.forEach(({ key, isVideo }) => {
            const urls = urlsByType[key] || [];
            urls.forEach((url) => {
                items.push({
                    displayUrl: getMediaDisplayUrl(memberId, key, url, apiResource),
                    url,
                    isVideo,
                    key,
                });
            });
        });
        return items;
    }, [memberId, urlsByType, apiResource]);

    const setUrls = (mediaType, urls) => {
        setUrlsByType((prev) => ({ ...prev, [mediaType]: urls }));
        onMediaUpdate?.();
    };

    const handleFiles = async (mediaType, files) => {
        if (!memberId || !files?.length) return;
        const list = Array.from(files);
        setUploading((p) => ({ ...p, [mediaType]: true }));
        try {
            const formData = new FormData();
            list.forEach((f) => formData.append('files', f));
            formData.append('mediaType', mediaType);
            const token = localStorage.getItem('token');
            const isVideoUpload = mediaType === 'standardVideos' || mediaType === 'icecreamVideos';
            const res = await axios.post(
                `${API_BASE}/api/${apiResource}/${memberId}/media`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true',
                    },
                    timeout: isVideoUpload ? 5 * 60 * 1000 : 60000,
                }
            );
            if (res.data.success) {
                const fromServer = res.data.data && res.data.data[mediaType];
                const added = res.data.added || [];
                setUrlsByType((prev) => {
                    const next = Array.isArray(fromServer) && fromServer.length > 0
                        ? fromServer
                        : [...(prev[mediaType] || []), ...added];
                    return { ...prev, [mediaType]: next };
                });
                onMediaUpdate?.();
            }
        } catch (err) {
            let msg = err.response?.data?.error || err.message || 'Upload failed';
            if (err.response?.status === 413 || err.message?.includes('413') || msg?.toLowerCase().includes('too large')) {
                msg = 'File too large (413). Video uploads need the server reverse proxy (e.g. nginx) to allow large bodies: set client_max_body_size 500m; then reload.';
            } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                msg = 'Upload timed out. Video conversion can take several minutes; try again or use a smaller file.';
            }
            console.error('Upload failed:', err);
            alert(msg);
        } finally {
            setUploading((p) => ({ ...p, [mediaType]: false }));
        }
    };

    const handleRemove = async (mediaType, url) => {
        if (!memberId) return;
        const deletedIndex = allMediaItems.findIndex((i) => i.key === mediaType && i.url === url);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE}/api/${apiResource}/${memberId}/media`, {
                data: { mediaType, url },
                headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' },
            });
            const nextUrls = urlsByType[mediaType].filter((u) => u !== url);
            setUrls(mediaType, nextUrls);
            setLightboxIndex((current) => {
                if (current == null) return null;
                if (deletedIndex === -1) return current;
                if (current === deletedIndex) return null;
                if (current > deletedIndex) return current - 1;
                return current;
            });
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const handleConfirmDeleteAllInCategory = async () => {
        if (!memberId || !deleteAllTarget) return;
        const { key } = deleteAllTarget;
        const urls = urlsByType[key] || [];
        if (urls.length === 0) {
            setDeleteAllTarget(null);
            return;
        }
        setDeleteAllLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE}/api/${apiResource}/${memberId}/media`, {
                data: { mediaType: key, deleteAll: true },
                headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' },
            });
            setUrlsByType((prev) => ({ ...prev, [key]: [] }));
            setLightboxIndex(null);
            setDeleteAllTarget(null);
            onMediaUpdate?.();
        } catch (err) {
            console.error('Delete all in category failed:', err);
            alert(err.response?.data?.error || err.message || 'Delete failed');
        } finally {
            setDeleteAllLoading(false);
        }
    };

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' };
    };

    const getFilenameFromUrl = (url) => (url.split('/').pop() || '').split('?')[0] || 'file';

    const handleDownloadAll = async () => {
        if (!memberId) return;
        const total = MEDIA_SECTIONS.reduce((acc, { key }) => acc + (urlsByType[key] || []).length, 0);
        if (total === 0) {
            alert('No media to download.');
            return;
        }
        setDownloadingAll(true);
        try {
            const zip = new JSZip();
            for (const { key } of MEDIA_SECTIONS) {
                const urls = urlsByType[key] || [];
                for (let i = 0; i < urls.length; i++) {
                    const url = urls[i];
                    const displayUrl = getMediaDisplayUrl(memberId, key, url);
                    const filename = getFilenameFromUrl(url);
                    const ext = filename.includes('.') ? filename.slice(filename.lastIndexOf('.')) : '';
                    const base = filename.includes('.') ? filename.slice(0, filename.lastIndexOf('.')) : filename;
                    const zipPath = `${key}/${i}_${base}${ext}`;
                    const res = await axios.get(displayUrl, { responseType: 'blob', headers: getAuthHeaders() });
                    zip.file(zipPath, res.data);
                }
            }
            const blob = await zip.generateAsync({ type: 'blob' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `media-${member?.godID || memberId}-${new Date().toISOString().slice(0, 10)}.zip`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            console.error('Download all failed:', err);
            alert('Download failed. Please try again.');
        } finally {
            setDownloadingAll(false);
        }
    };

    const handleDownloadSection = async (mediaType) => {
        if (!memberId) return;
        const urls = urlsByType[mediaType] || [];
        if (urls.length === 0) {
            alert(`No ${MEDIA_SECTIONS.find((s) => s.key === mediaType)?.label || mediaType} to download.`);
            return;
        }
        setDownloadingKey(mediaType);
        try {
            const zip = new JSZip();
            for (let i = 0; i < urls.length; i++) {
                const url = urls[i];
                const displayUrl = getMediaDisplayUrl(memberId, mediaType, url);
                const filename = getFilenameFromUrl(url);
                const ext = filename.includes('.') ? filename.slice(filename.lastIndexOf('.')) : '';
                const base = filename.includes('.') ? filename.slice(0, filename.lastIndexOf('.')) : filename;
                const zipPath = `${i}_${base}${ext}`;
                const res = await axios.get(displayUrl, { responseType: 'blob', headers: getAuthHeaders() });
                zip.file(zipPath, res.data);
            }
            const blob = await zip.generateAsync({ type: 'blob' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            const label = MEDIA_SECTIONS.find((s) => s.key === mediaType)?.label || mediaType;
            a.download = `${label.replace(/\s+/g, '-')}-${member?.godID || memberId}.zip`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            console.error('Download section failed:', err);
            alert('Download failed. Please try again.');
        } finally {
            setDownloadingKey(null);
        }
    };

    const openLightboxAtIndex = (index) => {
        if (index >= 0 && index < allMediaItems.length) setLightboxIndex(index);
    };

    const openLightboxFromItem = (displayUrl, url, isVideo, mediaType) => {
        const idx = allMediaItems.findIndex((i) => i.url === url && i.key === mediaType);
        if (idx !== -1) setLightboxIndex(idx);
    };

    // Keyboard left/right in lightbox
    useEffect(() => {
        if (lightboxIndex == null) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setLightboxIndex(null);
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setLightboxIndex((i) => Math.max(0, i - 1));
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                setLightboxIndex((i) => Math.min(allMediaItems.length - 1, i + 1));
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [lightboxIndex, allMediaItems.length]);

    const currentLightboxItem = lightboxIndex != null && allMediaItems[lightboxIndex] ? allMediaItems[lightboxIndex] : null;

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[1400px] max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header: title left, renewal right */}
                <div className="flex-shrink-0 border-b px-4 py-3 flex justify-between items-center gap-4">
                    <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                            <MediaTitleLeft member={member} apiResource={apiResource} />
                        </h3>
                        <button
                            type="button"
                            onClick={handleDownloadAll}
                            disabled={downloadingAll || MEDIA_SECTIONS.every(({ key }) => !(urlsByType[key] || []).length)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40 disabled:pointer-events-none"
                            title="Download all media as ZIP"
                            aria-label="Download all media"
                        >
                            <FiDownload className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        {apiResource === 'nonmembers' ? (
                            <span className="text-sm text-gray-600">
                                Setup: {member?.newSetupDone ? '✅' : '❌'}
                            </span>
                        ) : (
                            <span className={`text-sm ${getRenewalDateStyle(member?.renewalDate)}`}>
                                Renewal: {formatDate(member?.renewalDate)}
                            </span>
                        )}
                        <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700" aria-label="Close">
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Header row: 5 labels, each spanning 2 columns (10 cols total). Below: 2-col blocks per section; first row = upload + 1 image, then rows of 2 images; show all images. */}
                <div className="p-6 overflow-y-auto flex-1 min-h-0">
                    {(() => {
                        const maxRows = Math.max(
                            1,
                            ...MEDIA_SECTIONS.map(({ key }) => {
                                const n = (urlsByType[key] || []).length;
                                return 1 + Math.ceil(Math.max(0, n - 1) / 2);
                            })
                        );

                        const renderThumb = (mediaKey, url, displaySrc, isVideo) => (
                            <div key={url} className={`${THUMB_SIZE} relative group flex-shrink-0 min-w-0`}>
                                {isVideo ? (
                                    <video
                                        key={displaySrc}
                                        src={displaySrc}
                                        className={`${THUMB_SIZE} object-cover rounded-lg border border-gray-200 cursor-pointer hover:ring-2 hover:ring-blue-400`}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onClick={() => openLightboxFromItem(displaySrc, url, true, mediaKey)}
                                    />
                                ) : (
                                    <img
                                        key={displaySrc}
                                        src={displaySrc}
                                        alt=""
                                        className={`${THUMB_SIZE} object-cover rounded-lg border border-gray-200 cursor-pointer hover:ring-2 hover:ring-blue-400`}
                                        onClick={() => openLightboxFromItem(displaySrc, url, false, mediaKey)}
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemove(mediaKey, url);
                                    }}
                                    className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full opacity-90 group-hover:opacity-100"
                                >
                                    <FiX className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        );

                        return (
                            <div className="grid grid-cols-10 gap-y-2 gap-x-1">
                                {/* Header row: each label + download icon spans 2 columns */}
                                {MEDIA_SECTIONS.map(({ key, label }) => {
                                    const urls = urlsByType[key] || [];
                                    const hasMedia = urls.length > 0;
                                    return (
                                        <div key={key} className="col-span-2 flex items-center justify-center gap-1 py-1">
                                            <button
                                                type="button"
                                                onClick={() => hasMedia && setDeleteAllTarget({ key, label })}
                                                disabled={!hasMedia || deleteAllLoading}
                                                className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-40 disabled:pointer-events-none shrink-0"
                                                title={hasMedia ? `Delete all ${label}` : 'Nothing to delete'}
                                                aria-label={`Delete all ${label}`}
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                            <span className="text-sm font-medium text-gray-700">{label}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleDownloadSection(key)}
                                                disabled={!hasMedia || downloadingKey === key}
                                                className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40 disabled:pointer-events-none"
                                                title={`Download all ${label}`}
                                                aria-label={`Download ${label}`}
                                            >
                                                <FiDownload className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                                {/* Content rows: row 0 = upload + image0 (or col-span-2 upload when no images); row r>=1 = image(2r-1), image(2r) */}
                                {Array.from({ length: maxRows }, (_, rowIndex) =>
                                    MEDIA_SECTIONS.flatMap(({ key, accept, isVideo }) => {
                                        const urls = urlsByType[key] || [];
                                        const loading = uploading[key];
                                        const displayUrls = memberId ? urls.map((u) => getMediaDisplayUrl(memberId, key, u)) : urls;
                                        // Row 0: upload + image 0. Row r>=1: images at 1+(r-1)*2 and 2+(r-1)*2 (no duplicate of image 0)
                                        const leftIndex = rowIndex === 0 ? -1 : 1 + (rowIndex - 1) * 2;
                                        const rightIndex = rowIndex === 0 ? 0 : 2 + (rowIndex - 1) * 2;
                                        const leftUrl = leftIndex >= 0 && leftIndex < urls.length ? urls[leftIndex] : null;
                                        const rightUrl = rightIndex < urls.length ? urls[rightIndex] : null;
                                        const leftDisplay = leftIndex >= 0 && leftIndex < displayUrls.length ? displayUrls[leftIndex] : null;
                                        const rightDisplay = rightIndex < displayUrls.length ? displayUrls[rightIndex] : null;

                                        const uploadBox = (
                                            <div
                                                className={`${THUMB_SIZE} border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors flex-shrink-0 ${dragOverKey === key ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
                                                onClick={() => inputRefs.current[key]?.click()}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setDragOverKey(key);
                                                }}
                                                onDragLeave={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setDragOverKey(null);
                                                }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setDragOverKey(null);
                                                    const files = e.dataTransfer?.files;
                                                    if (files?.length) handleFiles(key, files);
                                                }}
                                            >
                                                <input
                                                    ref={(el) => (inputRefs.current[key] = el)}
                                                    type="file"
                                                    accept={accept}
                                                    multiple
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        handleFiles(key, e.target.files);
                                                        e.target.value = '';
                                                    }}
                                                />
                                                {loading ? (
                                                    <span className="text-xs text-blue-600">…</span>
                                                ) : (
                                                    <FiUpload className="w-8 h-8 text-gray-400" />
                                                )}
                                            </div>
                                        );

                                        if (rowIndex === 0 && urls.length === 0) {
                                            return [
                                                <div key={`${key}-${rowIndex}-0`} className="col-span-2 flex items-start justify-center min-w-0 pr-5">
                                                    {uploadBox}
                                                </div>,
                                            ];
                                        }

                                        return [
                                            /* Left cell: row 0 = upload box, else image */
                                            <div key={`${key}-${rowIndex}-0`} className="flex items-start justify-center min-w-0">
                                                {rowIndex === 0 ? (
                                                    uploadBox
                                                ) : leftUrl ? (
                                                    renderThumb(key, leftUrl, leftDisplay || leftUrl, isVideo)
                                                ) : (
                                                    <div className={`${THUMB_SIZE} rounded-lg border border-gray-100 bg-gray-50/50 flex-shrink-0`} />
                                                )}
                                            </div>,
                                            /* Right cell: row 0 = first image or empty, else image or empty; extra padding to separate sections */
                                            <div key={`${key}-${rowIndex}-1`} className="flex items-start justify-center min-w-0 pr-5">
                                                {rightUrl ? (
                                                    renderThumb(key, rightUrl, rightDisplay || rightUrl, isVideo)
                                                ) : (
                                                    <div className={`${THUMB_SIZE} rounded-lg border border-gray-100 bg-gray-50/50 flex-shrink-0`} />
                                                )}
                                            </div>,
                                        ];
                                    })
                                ).flat()}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {deleteAllTarget && (
                <DeleteConfirmationModal
                    title={`Delete all ${deleteAllTarget.label}?`}
                    message="This removes every file in this category from cloud storage (R2) and clears it on the member record. This cannot be undone."
                    itemName={deleteAllTarget.label}
                    isLoading={deleteAllLoading}
                    onCancel={() => !deleteAllLoading && setDeleteAllTarget(null)}
                    onConfirm={handleConfirmDeleteAllInCategory}
                />
            )}

            {/* Lightbox: view in big, prev/next across all media */}
            {currentLightboxItem && (
                <div
                    className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setLightboxIndex(null)}
                >
                    <button
                        type="button"
                        onClick={() => setLightboxIndex(null)}
                        className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full z-10"
                        aria-label="Close"
                    >
                        <FiX className="w-8 h-8" />
                    </button>

                    {allMediaItems.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openLightboxAtIndex(Math.max(0, lightboxIndex - 1));
                                }}
                                disabled={lightboxIndex <= 0}
                                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white hover:bg-white/20 rounded-full disabled:opacity-40 disabled:pointer-events-none z-10"
                                aria-label="Previous"
                            >
                                <FiChevronLeft className="w-10 h-10" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openLightboxAtIndex(Math.min(allMediaItems.length - 1, lightboxIndex + 1));
                                }}
                                disabled={lightboxIndex >= allMediaItems.length - 1}
                                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white hover:bg-white/20 rounded-full disabled:opacity-40 disabled:pointer-events-none z-10"
                                aria-label="Next"
                            >
                                <FiChevronRight className="w-10 h-10" />
                            </button>
                        </>
                    )}

                    <div className="text-white text-sm absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                        {lightboxIndex + 1} / {allMediaItems.length}
                    </div>

                    <div className="max-w-full max-h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        {currentLightboxItem.isVideo ? (
                            <video
                                key={currentLightboxItem.displayUrl}
                                src={currentLightboxItem.displayUrl}
                                controls
                                autoPlay
                                className="max-w-full max-h-[85vh] rounded"
                            />
                        ) : (
                            <img
                                key={currentLightboxItem.displayUrl}
                                src={currentLightboxItem.displayUrl}
                                alt=""
                                className="max-w-full max-h-[85vh] object-contain rounded"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

GodViewMediaModal.propTypes = {
    member: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        godID: PropTypes.string,
        godName: PropTypes.string,
        webID: PropTypes.string,
        oldNames: PropTypes.arrayOf(PropTypes.string),
        renewalDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        fullName: PropTypes.string,
        wechatQR: PropTypes.arrayOf(PropTypes.string),
        standardPhotos: PropTypes.arrayOf(PropTypes.string),
        icecreamPhotos: PropTypes.arrayOf(PropTypes.string),
        standardVideos: PropTypes.arrayOf(PropTypes.string),
        icecreamVideos: PropTypes.arrayOf(PropTypes.string),
    }),
    onClose: PropTypes.func.isRequired,
    onMediaUpdate: PropTypes.func,
};

export default GodViewMediaModal;
